import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    getExpenseTypes, createExpenseType, updateExpenseType, deleteExpenseType,
    listVendorMappings, createVendorMapping, updateVendorMapping, deleteVendorMapping, backfillVendorMappings,
    type ExpenseType, type VendorMapping, type VendorSourceType,
} from '../api'
import { useDataVersion } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import { useServerList } from '../hooks/useServerList'
import ListToolbar from '../components/ListToolbar'
import ListPanel from '../components/ListPanel'
import Pagination from '../components/Pagination'
import DataTable, { type Column } from '../components/DataTable'
import ConfirmDialog from '../components/ConfirmDialog'
import { checkWithin } from '../forms/checkForm'
import { DEFAULT_TABLE_PAGE_SIZE } from '../../shared/constants'
import { fill, PAGES_EXPENSE_MAPPING_ADMIN_TEXTS as T } from '../../shared/texts'

const EditIcon = () => <img src="/icons/edit.svg" alt="" aria-hidden="true" />
const TrashIcon = () => <img src="/icons/delete.svg" alt="" aria-hidden="true" />
const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12" />
    </svg>
)
const XIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
)

type EditRow = { id: string, domain: string, vendorName: string, expenseType: string, sourceType: VendorSourceType }
type DelTarget = { kind: 'type' | 'mapping', id: string, label: string }

const SOURCE_LABELS: Record<VendorSourceType, string> = { pdf: T.sourcePdf, link: T.sourceLink }

export default function ExpenseMappingAdmin() {
    const { toast } = useToast()
    const { can } = useAuth()
    const canWrite = can('expense-mapping', 'write')
    const canDelete = can('expense-mapping', 'delete')
    const version = useDataVersion('vendor-mappings')
    const [bump, setBump] = useState(0) // local refetch trigger for the mappings list
    const list = useServerList<VendorMapping>(listVendorMappings, version + bump, { sort: 'domain', dir: 'asc', pageSize: DEFAULT_TABLE_PAGE_SIZE })

    const [error, setError] = useState('')
    const [busy, setBusy] = useState(false)
    // Domains are edited far more often than expense types → show them first, types behind a pill.
    const [tab, setTab] = useState<'domains' | 'types'>('domains')

    // --- Expense types ---
    const [types, setTypes] = useState<ExpenseType[]>([])
    const [newType, setNewType] = useState('')
    const [editTypeId, setEditTypeId] = useState<string | null>(null)
    const [editTypeLabel, setEditTypeLabel] = useState('')
    const reloadTypes = useCallback(() => getExpenseTypes().then(setTypes).catch(() => {}), [])
    useEffect(() => { reloadTypes() }, [reloadTypes])
    const typeLabel = useMemo(() => {
        const m = new Map(types.map((t) => [t.key, t.label]))
        return (key: string) => m.get(key) ?? key
    }, [types])
    // Types are fully loaded → search + paginate client-side (same ListPanel + Pagination UI as the domains).
    const [typeQ, setTypeQ] = useState('')
    const [typePage, setTypePage] = useState(1)
    const [typePageSize, setTypePageSize] = useState(DEFAULT_TABLE_PAGE_SIZE)
    const filteredTypes = useMemo(() => {
        const q = typeQ.trim().toLowerCase()
        return q ? types.filter((t) => t.label.toLowerCase().includes(q) || t.key.toLowerCase().includes(q)) : types
    }, [types, typeQ])
    const typePageCount = Math.max(1, Math.ceil(filteredTypes.length / typePageSize))
    const typeShownPage = Math.min(typePage, typePageCount)
    const pagedTypes = filteredTypes.slice((typeShownPage - 1) * typePageSize, typeShownPage * typePageSize)

    async function run(fn: () => Promise<void>) {
        setBusy(true); setError('')
        try { await fn() }
        catch (e: any) { setError(e?.response?.data?.error ?? e?.message ?? T.error) }
        finally { setBusy(false) }
    }

    const addNewType = () => {
        const label = newType.trim()
        if (!label) return
        run(async () => { await createExpenseType(label); setNewType(''); await reloadTypes() })
    }
    const saveType = (id: string, e?: { currentTarget: EventTarget | null }) => {
        if (!checkWithin(e, 'tr')) return
        const label = editTypeLabel.trim()
        if (!label) return
        run(async () => { await updateExpenseType(id, label); setEditTypeId(null); await reloadTypes(); setBump((b) => b + 1) })
    }

    // --- Mappings: add + inline edit ---
    const [addDomain, setAddDomain] = useState('')
    const [addVendor, setAddVendor] = useState('')
    const [addType, setAddType] = useState('')
    const [addSource, setAddSource] = useState<VendorSourceType>('pdf')
    const addMapping = (e?: { currentTarget: EventTarget | null }) => {
        if (!checkWithin(e, '.check-add')) return
        run(async () => {
            await createVendorMapping({ domain: addDomain.trim(), vendorName: addVendor.trim() || undefined, expenseType: addType, sourceType: addSource })
            setAddDomain(''); setAddVendor(''); setAddType(''); setAddSource('pdf')
            setBump((b) => b + 1)
        })
    }

    const [edit, setEdit] = useState<EditRow | null>(null)
    const startEdit = (r: VendorMapping) => setEdit({ id: r._id, domain: r.domain, vendorName: r.vendorName, expenseType: r.expenseType, sourceType: r.sourceType })
    const cancelEdit = () => setEdit(null)
    const saveEdit = (e?: { currentTarget: EventTarget | null }) => {
        if (!checkWithin(e, 'tr')) return
        if (!edit) return
        run(async () => {
            await updateVendorMapping(edit.id, { domain: edit.domain.trim(), vendorName: edit.vendorName.trim(), expenseType: edit.expenseType, sourceType: edit.sourceType })
            setEdit(null); setBump((b) => b + 1)
        })
    }

    // --- Delete (shared dialog for types + mappings) ---
    const [del, setDel] = useState<DelTarget | null>(null)
    const [delBusy, setDelBusy] = useState(false)
    const [delError, setDelError] = useState('')
    async function confirmDelete() {
        if (!del) return
        setDelBusy(true); setDelError('')
        try {
            if (del.kind === 'type') { await deleteExpenseType(del.id); await reloadTypes() }
            else { await deleteVendorMapping(del.id); setBump((b) => b + 1) }
            setDel(null)
        } catch (e: any) {
            setDelError(e?.response?.data?.error ?? T.deleteFailed)
        } finally { setDelBusy(false) }
    }

    const doBackfill = () => run(async () => {
        const r = await backfillVendorMappings()
        toast(fill(T.backfillDone, { count: r.updated }))
        setBump((b) => b + 1)
    })

    const typeOptions = (value: string, onChange: (v: string) => void, label?: string) => (
        <select value={value} required aria-label={label} onChange={(e) => onChange(e.target.value)}>
            <option value="">{T.chooseType}</option>
            {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
    )

    const sourceOptions = (value: VendorSourceType, onChange: (v: VendorSourceType) => void) => (
        <select value={value} onChange={(e) => onChange(e.target.value as VendorSourceType)} title={T.sourceTitle}>
            <option value="pdf">{SOURCE_LABELS.pdf}</option>
            <option value="link">{SOURCE_LABELS.link}</option>
        </select>
    )

    const columns: Column<VendorMapping>[] = [
        {
            key: 'domain', header: T.colDomain, sortField: 'domain', defaultWidth: 260,
            render: (r) => edit?.id === r._id
                ? <input type="text" value={edit.domain} required onChange={(e) => setEdit({ ...edit, domain: e.target.value })} />
                : <code>{r.domain}</code>,
        },
        {
            key: 'vendorName', header: T.colVendor, sortField: 'vendorName', defaultWidth: 240,
            render: (r) => edit?.id === r._id
                ? <input type="text" value={edit.vendorName} onChange={(e) => setEdit({ ...edit, vendorName: e.target.value })} />
                : (r.vendorName || <span className="muted">—</span>),
        },
        {
            key: 'expenseType', header: T.colExpenseType, sortField: 'expenseType', defaultWidth: 240,
            render: (r) => edit?.id === r._id
                ? typeOptions(edit.expenseType, (v) => setEdit({ ...edit, expenseType: v }))
                : typeLabel(r.expenseType),
        },
        {
            key: 'sourceType', header: T.colSource, defaultWidth: 150,
            render: (r) => edit?.id === r._id
                ? sourceOptions(edit.sourceType, (v) => setEdit({ ...edit, sourceType: v }))
                : SOURCE_LABELS[r.sourceType] ?? SOURCE_LABELS.pdf,
        },
        {
            key: 'actions', header: T.colActions, defaultWidth: 116, sticky: true,
            render: (r) => edit?.id === r._id ? (
                <div className="row-actions">
                    <button className="icon-action" title={T.save} aria-label={T.save} disabled={busy} onClick={(e) => saveEdit(e)}><CheckIcon /></button>
                    <button type="button" className="icon-action" title={T.cancel} aria-label={T.cancel} disabled={busy} onClick={cancelEdit}><XIcon /></button>
                </div>
            ) : (
                <div className="row-actions">
                    {canWrite && <button className="icon-action" title={T.edit} aria-label={T.edit} onClick={() => startEdit(r)}><EditIcon /></button>}
                    {canDelete && <button className="icon-action" title={T.delete} aria-label={T.delete} onClick={() => setDel({ kind: 'mapping', id: r._id, label: r.domain })}><TrashIcon /></button>}
                    {!canWrite && !canDelete && <span className="muted">—</span>}
                </div>
            ),
        },
    ]

    // Same DataTable layout as the domains: header row + right-docked actions.
    const typeColumns: Column<ExpenseType>[] = [
        {
            key: 'label', header: T.colLabel, defaultWidth: 300,
            render: (t) => editTypeId === t._id
                ? <input type="text" value={editTypeLabel} autoFocus required
                    onChange={(e) => setEditTypeLabel(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveType(t._id, e); if (e.key === 'Escape') setEditTypeId(null) }} />
                : t.label,
        },
        {
            key: 'key', header: T.colKey, defaultWidth: 220,
            render: (t) => <code className="muted">{t.key}</code>,
        },
        {
            key: 'actions', header: T.colActions, defaultWidth: 116, sticky: true,
            render: (t) => editTypeId === t._id ? (
                <div className="row-actions">
                    <button className="icon-action" title={T.save} aria-label={T.save} disabled={busy} onClick={(e) => saveType(t._id, e)}><CheckIcon /></button>
                    <button type="button" className="icon-action" title={T.cancel} aria-label={T.cancel} disabled={busy} onClick={() => setEditTypeId(null)}><XIcon /></button>
                </div>
            ) : (
                <div className="row-actions">
                    {canWrite && <button className="icon-action" title={T.rename} aria-label={T.rename} onClick={() => { setEditTypeId(t._id); setEditTypeLabel(t.label) }}><EditIcon /></button>}
                    {canDelete && <button className="icon-action" title={T.delete} aria-label={T.delete} onClick={() => setDel({ kind: 'type', id: t._id, label: t.label })}><TrashIcon /></button>}
                    {!canWrite && !canDelete && <span className="muted">—</span>}
                </div>
            ),
        },
    ]

    return (
        <div className="list-page">
            <ListToolbar title={T.title}
                infoText={T.info}
                action={canWrite ? <button type="button" className="secondary" disabled={busy} onClick={doBackfill}>{T.backfill}</button> : undefined} />

            {(error || list.error) && <div className="notice err">{error || list.error}</div>}

            <div className="pill-tabs" role="tablist">
                <button type="button" role="tab" className={`pill-tab${tab === 'domains' ? ' active' : ''}`}
                    aria-selected={tab === 'domains'} onClick={() => setTab('domains')}>
                    {T.tabDomains}{list.grandTotal ? ` (${list.grandTotal})` : ''}
                </button>
                <button type="button" role="tab" className={`pill-tab${tab === 'types' ? ' active' : ''}`}
                    aria-selected={tab === 'types'} onClick={() => setTab('types')}>
                    {T.tabTypes}{types.length ? ` (${types.length})` : ''}
                </button>
            </div>

            {tab === 'types' && (
            <>
            {/* Add type */}
            {canWrite && (
            <div className="panel">
                <h2>{T.addTypeHeading}</h2>
                <div className="row check-add">
                    <input type="text" placeholder={T.newTypePlaceholder} value={newType}
                        onChange={(e) => setNewType(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') addNewType() }} />
                    <button className="secondary" disabled={busy || !newType.trim()} onClick={addNewType}>{T.add}</button>
                </div>
            </div>
            )}

            {/* Types list */}
            <ListPanel
                count={filteredTypes.length}
                grandTotal={types.length}
                filtered={typeQ.trim() !== ''}
                search={{ value: typeQ, onChange: (v) => { setTypeQ(v); setTypePage(1) }, placeholder: T.typeSearchPlaceholder }}
            >
                {filteredTypes.length === 0 ? (
                    <p className="muted">{typeQ ? T.noTypesFound : T.noTypesYet}</p>
                ) : (
                    <DataTable tableId="expense-types" columns={typeColumns} rows={pagedTypes} rowKey={(t) => t._id} />
                )}
            </ListPanel>
            <Pagination page={typeShownPage} pageCount={typePageCount} onPage={setTypePage}
                pageSize={typePageSize} onPageSize={(n) => { setTypePageSize(n); setTypePage(1) }} />
            </>
            )}

            {tab === 'domains' && (
            <>
            {/* Add mapping */}
            {canWrite && (
            <div className="panel">
                <h2>{T.addMappingHeading}</h2>
                <div className="row check-add">
                    <input type="text" placeholder={T.domainPlaceholder} aria-label={T.colDomain} required value={addDomain} onChange={(e) => setAddDomain(e.target.value)} />
                    <input type="text" placeholder={T.vendorPlaceholder} value={addVendor} onChange={(e) => setAddVendor(e.target.value)} />
                    {typeOptions(addType, setAddType, T.colExpenseType)}
                    {sourceOptions(addSource, setAddSource)}
                    <button className="secondary" disabled={busy} onClick={(e) => addMapping(e)}>{T.add}</button>
                </div>
            </div>
            )}

            {/* Mappings list */}
            <ListPanel
                count={list.total}
                grandTotal={list.grandTotal}
                filtered={list.q.trim() !== ''}
                search={{ value: list.q, onChange: list.setQ }}
            >
                {list.loading ? <p className="muted">{T.loading}</p> : list.rows.length === 0 ? (
                    <p className="muted">{T.noMappings}</p>
                ) : (
                    <DataTable tableId="vendor-mappings" columns={columns} rows={list.rows}
                        rowKey={(r) => r._id} sort={list.sort} dir={list.dir} onSort={list.toggleSort} />
                )}
            </ListPanel>
            <Pagination page={list.page} pageCount={list.pageCount} onPage={list.setPage} pageSize={list.pageSize} onPageSize={list.setPageSize} />
            </>
            )}

            <ConfirmDialog
                open={!!del}
                title={del?.kind === 'type' ? T.deleteType : T.deleteMapping}
                message={del ? <>{fill(T.deleteConfirm, { label: del.label })}</> : null}
                confirmLabel={T.delete}
                danger
                busy={delBusy}
                error={delError}
                onConfirm={confirmDelete}
                onCancel={() => { if (!delBusy) { setDel(null); setDelError('') } }}
            />
        </div>
    )
}
